import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Table, Tag, Typography } from '@thinknet-gmbh/platform-common-ui';
import { EmployeeStatusesEnum } from '@thinknet-gmbh/platform-common';

import { EMPLOYEE_STATUSES_STYLES } from 'src/pages/employee/sub-pages/employee-list/employee-list.constants';
import { EMPLOYEE_PAGES } from 'src/pages/employee/employee.constants';
import { ROUTES } from 'src/components/app/app-routes.constants';

import EmployeeTableRowPopover from './employee-table-row-popover.component';
import { IEmployeeTableProps } from './employee-list-table.interfaces';

const EmployeeListTable = ({
  dataSource,
  paginationConfig,
  handleModals,
  limitedView,
  onTableChange,
  loading,
}: IEmployeeTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'employee']);

  return (
    <Table
      loading={loading}
      dataSource={dataSource}
      pagination={paginationConfig}
      rowKey={record => record._id}
      onRow={({ _id }) => ({
        onClick: () =>
          navigate(
            `${ROUTES.employee}?type=${
              limitedView ? EMPLOYEE_PAGES.employeeLimitedView : EMPLOYEE_PAGES.employeeProfile
            }&id=${_id}`
          ),
      })}
      t={t}
    >
      <Table.Column
        title={t('employee:labels.employeeNumber')}
        render={employee => (
          <Typography>{employee?.employeePersonalInfo?.employeeNumber}</Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.fullName')}
        sorter={onTableChange}
        render={({ profile }) => (
          <Typography>
            {profile.firstName} {profile.lastName}
          </Typography>
        )}
      />
      <Table.Column
        title={t('employee:labels.company')}
        render={({ activeWorkingCondition }) => (
          <Typography>{activeWorkingCondition?.company?.name || '-'}</Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.location')}
        render={({ activeWorkingCondition }) => (
          <Typography>{activeWorkingCondition?.companyLocation?.name || '-'}</Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.department')}
        render={({ activeWorkingCondition }) => (
          <Typography>{activeWorkingCondition?.companyDepartment?.name || '-'}</Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.jobTitle')}
        render={({ activeWorkingCondition }) => (
          <Typography>{activeWorkingCondition?.job?.title || '-'}</Typography>
        )}
      />
      <Table.Column
        title={t('employee:labels.supervisor')}
        render={({ activeWorkingCondition }) => {
          let displaySupervisor = '-';

          if (activeWorkingCondition) {
            displaySupervisor = activeWorkingCondition.jobSupervisor.supervisorJobTitle;

            if (activeWorkingCondition.jobSupervisor.supervisorMember) {
              displaySupervisor += ` (${activeWorkingCondition.jobSupervisor.supervisorMember.profile.displayName})`;
            }
          }

          return <Typography>{displaySupervisor}</Typography>;
        }}
      />
      <Table.Column
        title={t('common:labels.status')}
        render={employee => {
          const { background, fontColor } =
            EMPLOYEE_STATUSES_STYLES[
              employee?.employeePersonalInfo
                ? (employee.employeePersonalInfo.status as EmployeeStatusesEnum)
                : EmployeeStatusesEnum.ACTIVE
            ];

          return (
            <Tag background={background} fontColor={fontColor}>
              {t(
                `employee:employeePersonalInfo.labels.statuses.${employee?.employeePersonalInfo?.status.toLowerCase()}`
              ).toLowerCase()}
            </Tag>
          );
        }}
      />
      <Table.Column
        title={t('common:labels.creationDate')}
        key="createdAt"
        dataIndex="createdAt"
        render={createdAt => <Typography>{moment(createdAt).format('DD.MM.YYYY')}</Typography>}
      />
      <Table.Column
        width={50}
        title={t('common:labels.action')}
        render={record => (
          <EmployeeTableRowPopover
            record={record}
            handleModals={handleModals}
            limitedView={limitedView}
          />
        )}
      />
    </Table>
  );
};

export default EmployeeListTable;
